// ItemActions — unified per-item action cluster for content views.
//
// 19BJ Session 3: extracted from quiz-phases.tsx where ItemActionMenu +
// FlagButton were hand-wired side-by-side (~15 lines × 2 sites). Every
// content view that shows an item needs the same cluster, so wrapping it
// here keeps the integration site a 1-liner and gives a single extension
// point (future actions: share / bookmark-from-here / etc.).
//
// Design doc: docs/architecture/regen-embedded-design.md §2.1 (refined —
// the doc's "ItemActionMenu alongside FlagButton" is realised by this
// compound component instead of manual pairing at each call site).
//
// UX boundary (Audit B1.3):
//   - ItemActionMenu (regen): "I want more / different content" → active regen
//   - FlagButton (flag):      "This content is wrong" → mark + wait for repair
//   The two are optional companions, never merged. New views default to
//   regen-only; pass `flagKind` to also surface an item-level flag.
//
// R6 deviation (陛下 approved): RegenConfirmPanel is retained for Regen
// Center batch confirm; RegenPreview inline mode (used by ItemActionMenu)
// serves single-item embedded regen. The two coexist.

import { ItemActionMenu } from './item-action-menu.js';
import { FlagButton } from './flag-button.js';
import type { PartialRegenStage } from '../storage/idb.js';
import type { RepairableKind } from '../api/repair.js';

// ── Types ──────────────────────────────────────────────────────────────────────

/** Minimal item shape accepted by ItemActions. Real items (MCQ / Flashcard /
 *  Essay / Glossary term / …) carry many more fields; we only need identity +
 *  scoping. `caseId` is singular (mcq/tf/flashcard/essay/freeResponse/proof),
 *  `caseIds` is plural (glossary) — normalizeCaseIds unifies them. */
export interface ItemActionsItem {
  id: string;
  caseId?: string;
  caseIds?: string[];
  conceptIds?: string[];
}

export interface ItemActionsProps {
  /** Course ID — required for regen API calls. */
  courseId: string;
  /** The item to operate on. */
  item: ItemActionsItem;
  /** Which stage this item belongs to. */
  stage: PartialRegenStage;
  /** Called after regen is accepted and merged (usually refreshProgress). */
  onRegenComplete?: () => void;
  /** Optional item-level FlagButton. Omit for regen-only (new view default).
   *  Passing `flagKind` without `flagItemJson` renders a flag without the
   *  AI-repair affordance (mark/rate only). */
  flagKind?: RepairableKind;
  flagItemJson?: unknown;
  flagContextItems?: unknown[];
}

// ── Helpers ────────────────────────────────────────────────────────────────────

/** Unify singular `caseId` / plural `caseIds` into a single array.
 *  Pure function — exported for invariant testing. */
export function normalizeCaseIds(item: ItemActionsItem): string[] {
  if (item.caseIds && item.caseIds.length > 0) return item.caseIds;
  if (item.caseId) return [item.caseId];
  return [];
}

/** Resolve the chapter scope for regen. Prefers the explicit singular
 *  `caseId`; when only `caseIds` is present, uses it only when there is
 *  exactly one (a multi-case item like a glossary term is course-level —
 *  chapterId stays undefined so add-mode falls back to itemId scope). */
export function resolveChapterId(item: ItemActionsItem): string | undefined {
  if (item.caseId) return item.caseId;
  const ids = normalizeCaseIds(item);
  return ids.length === 1 ? ids[0] : undefined;
}

// ── Component ──────────────────────────────────────────────────────────────────

export function ItemActions({
  courseId,
  item,
  stage,
  onRegenComplete,
  flagKind,
  flagItemJson,
  flagContextItems,
}: ItemActionsProps) {
  const caseIds = normalizeCaseIds(item);
  const chapterId = resolveChapterId(item);

  // Build the repairable payload only when an itemJson was supplied —
  // a flag without repair is still useful (mark/rate/note).
  const repairable = flagKind && flagItemJson !== undefined
    ? {
        kind: flagKind,
        itemJson: flagItemJson,
        ...(flagContextItems ? { contextItems: flagContextItems } : {}),
      }
    : undefined;

  return (
    <span className="item-actions">
      <ItemActionMenu
        courseId={courseId}
        item={{
          id: item.id,
          caseIds,
          ...(item.conceptIds ? { conceptIds: item.conceptIds } : {}),
        }}
        stage={stage}
        chapterId={chapterId}
        onRegenComplete={onRegenComplete}
      />
      {flagKind && (
        <FlagButton
          itemId={item.id}
          field="overall"
          {...(repairable ? { repairable } : {})}
        />
      )}
    </span>
  );
}
